import { useEffect, useRef, useState, useCallback } from 'react';
import { saveWorksheet } from '../data/worksheetsApi';

// One writer per mounted editor. A save in progress is never allowed to overwrite
// a newer edit, and a failed save retains the browser recovery copy.
export function useWorksheetDraft({ editor, userId, worksheetId, templateId, title, sourcePrompt, metadata, ready, onPersisted }) {
  const [status, setStatus] = useState(worksheetId ? 'saved' : 'idle');
  const [recovery, setRecovery] = useState(null);
  const id = useRef(worksheetId);
  const revision = useRef(0);
  const savedRevision = useRef(0);
  const running = useRef(null);
  const paused = useRef(false);
  const timer = useRef(null);
  const mounted = useRef(true);
  const latest = useRef();
  const key = `worksheep:draft:${userId || 'guest'}:${worksheetId || 'new'}`;
  latest.current = { editor, userId, templateId, title, sourcePrompt, metadata, key, onPersisted };

  const snapshot = useCallback(() => {
    const v = latest.current;
    return { title: v.title, content: { ...v.editor.getJSON(), worksheetMeta: v.metadata }, updatedAt: Date.now() };
  }, []);

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    if (running.current) {
      await running.current;
      if (savedRevision.current < revision.current) return save();
      return { ok: savedRevision.current === revision.current, id: id.current };
    }
    const v = latest.current;
    if (!mounted.current) return { ok: false };
    if (!v.editor || !v.userId || paused.current) return { ok: false };
    const atRevision = revision.current;
    const draft = snapshot();
    setStatus('saving');
    const task = (async () => {
      try {
        const { data, error } = await saveWorksheet({ id: id.current, userId: v.userId, templateId: v.templateId,
          title: draft.title, content: draft.content, sourcePrompt: v.sourcePrompt });
        if (error || !data?.id) throw error || new Error('Save failed');
        id.current = data.id;
        savedRevision.current = atRevision;
        if (mounted.current) setStatus(revision.current === atRevision ? 'saved' : 'unsaved');
        if (revision.current === atRevision) {
          if (!worksheetId && mounted.current) v.onPersisted?.(data.id);
          try { localStorage.removeItem(v.key); } catch { /* storage unavailable */ }
        }
        return { ok: true, id: data.id };
      } catch {
        if (mounted.current) setStatus('error');
        return { ok: false };
      }
    })();
    running.current = task;
    const result = await task;
    running.current = null;
    return result;
  }, [snapshot, worksheetId]);

  const changed = useCallback(() => {
    if (!latest.current.editor || !latest.current.userId || paused.current) return;
    revision.current += 1;
    setStatus('unsaved');
    try { localStorage.setItem(latest.current.key, JSON.stringify(snapshot())); } catch { /* quota/private mode */ }
    clearTimeout(timer.current);
    timer.current = setTimeout(save, 1000);
  }, [save, snapshot]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearTimeout(timer.current); };
  }, []);

  useEffect(() => {
    if (!ready || !editor || !userId) return;
    let cached;
    try { cached = JSON.parse(localStorage.getItem(key)); } catch { /* invalid cache */ }
    if (cached?.content) { paused.current = true; setRecovery(cached); }
    else if (!worksheetId) changed();
    editor.on('update', changed);
    return () => { editor.off('update', changed); clearTimeout(timer.current); };
  }, [editor, ready, userId, key, worksheetId, changed]);

  const previous = useRef({ title, metadata });
  useEffect(() => {
    const old = previous.current;
    previous.current = { title, metadata };
    if (ready && (old.title !== title || old.metadata !== metadata)) changed();
  }, [title, metadata, ready, changed]);

  useEffect(() => {
    if (!['unsaved', 'saving', 'error'].includes(status)) return;
    const guard = e => { e.preventDefault(); e.returnValue = ''; };
    const navigation = e => {
      const link = e.target.closest?.('a[href]');
      if (!link || link.href === window.location.href) return;
      if (!window.confirm('Your latest changes are not saved yet. Leave this page? A recovery copy is kept in this browser.')) {
        e.preventDefault(); e.stopImmediatePropagation();
      }
    };
    window.addEventListener('beforeunload', guard);
    document.addEventListener('click', navigation, true);
    return () => { window.removeEventListener('beforeunload', guard); document.removeEventListener('click', navigation, true); };
  }, [status]);

  return { status, save, recovery, clearRecovery: () => {
    paused.current = false;
    setRecovery(null);
    try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
  }, changed };
}
